import {
  ChangeRequestArea,
  ChangeRequestAreaMap,
  ChangeRequestStatus,
  ChangeRequestStatusMap,
} from 'respona_api/generated/order-publisher_pb';
import { SvgIconType } from '@uikit/old/Icon/Icon';
import { PRIMARY_COLOR, SUCCESS_COLOR, WARNING_COLOR } from '@constants/colors';

export type ChangeRequestStatusValueType = ChangeRequestStatusMap[keyof ChangeRequestStatusMap];

/** How long the "Copied" confirmation stays next to the order number. */
export const COPIED_LABEL_TIMEOUT_MS = 1500;

/**
 * Copy for the pipeline link-insertion brief. It is an instruction for the
 * publisher, not an article to publish, and the copy has to say so.
 */
export const INSERTION_BRIEF_TITLE = 'Insertion brief';
export const INSERTION_BRIEF_HINT =
  'Where in your article the link goes. Open the Google Doc, insert the link as described, then submit the live URL.';
export const INSERTION_BRIEF_LINK_LABEL = 'Open the brief';
/** Shown under the live URL / edit URL input when the value is not an http(s) URL. */
export const INVALID_LIVE_URL_ERROR = 'Enter a valid URL (http or https)';
/** Props for the shared `InsertionBrief` card in the Publisher realm. */
export const INSERTION_BRIEF_COPY = {
  title: INSERTION_BRIEF_TITLE,
  hint: INSERTION_BRIEF_HINT,
  linkLabel: INSERTION_BRIEF_LINK_LABEL,
} as const;

export type ChangeRequestStatusConfigType = {
  title: string;
  icon: SvgIconType;
  iconColor: string;
  /** BEM modifier for the inline table pill. */
  modifier: string;
  /** Colours for the sidebar header pill, which is styled inline. */
  textColor: string;
  backgroundColor: string;
};

export const changeRequestStatusConfig: Partial<
  Record<ChangeRequestStatusValueType, ChangeRequestStatusConfigType>
> = {
  [ChangeRequestStatus.CHANGES_REQUESTED]: {
    title: 'Concern raised',
    icon: 'exclamationMarkInCircleIcon',
    iconColor: WARNING_COLOR,
    modifier: 'orange',
    textColor: WARNING_COLOR,
    backgroundColor: '#FFF1E2',
  },
  [ChangeRequestStatus.CHANGE_REQUEST_IN_REVIEW]: {
    title: 'In review',
    icon: 'clockThree',
    iconColor: PRIMARY_COLOR,
    modifier: 'blue',
    textColor: PRIMARY_COLOR,
    backgroundColor: '#E9EEFF',
  },
  [ChangeRequestStatus.REVISING_DRAFT]: {
    title: 'Revising draft',
    icon: 'clockThree',
    iconColor: PRIMARY_COLOR,
    modifier: 'blue',
    textColor: PRIMARY_COLOR,
    backgroundColor: '#E9EEFF',
  },
  [ChangeRequestStatus.CHANGE_REQUEST_RESOLVED]: {
    title: 'Concern resolved',
    icon: 'successCircleFilled',
    iconColor: SUCCESS_COLOR,
    modifier: 'green',
    textColor: '#1F8A5B',
    backgroundColor: '#EBFFF3',
  },
};

/**
 * True when the order carries a change request at all, resolved or not.
 * Resolved ones stay openable so publishers can read the history back.
 */
export const hasChangeRequest = (status?: ChangeRequestStatusValueType): boolean =>
  status != null && status !== ChangeRequestStatus.CHANGE_REQUEST_STATUS_UNSPECIFIED;

export type ChangeRequestAreaItemType = {
  title: string;
  icon: SvgIconType | 'T';
  iconColor: string;
  iconSize: number;
  value: ChangeRequestAreaMap[keyof ChangeRequestAreaMap];
};

export const CHANGE_REQUEST_AREA_ICON_COLOR = '#525979';

export const changeRequestAreaList: ChangeRequestAreaItemType[] = [
  {
    title: 'Target URL',
    icon: 'link',
    iconColor: CHANGE_REQUEST_AREA_ICON_COLOR,
    iconSize: 16,
    value: ChangeRequestArea.CHANGE_REQUEST_AREA_TARGET_URL,
  },
  {
    title: 'Anchor',
    icon: 'T',
    iconColor: CHANGE_REQUEST_AREA_ICON_COLOR,
    iconSize: 16,
    value: ChangeRequestArea.CHANGE_REQUEST_AREA_ANCHOR,
  },
  {
    title: 'Content',
    icon: 'blank',
    iconColor: CHANGE_REQUEST_AREA_ICON_COLOR,
    iconSize: 16,
    value: ChangeRequestArea.CHANGE_REQUEST_AREA_CONTENT,
  },
  {
    title: 'Placement',
    icon: 'globe',
    iconColor: CHANGE_REQUEST_AREA_ICON_COLOR,
    iconSize: 16,
    value: ChangeRequestArea.CHANGE_REQUEST_AREA_PLACEMENT,
  },
  {
    title: 'Other',
    icon: 'more',
    iconColor: CHANGE_REQUEST_AREA_ICON_COLOR,
    iconSize: 16,
    value: ChangeRequestArea.CHANGE_REQUEST_AREA_OTHER,
  },
];
